import React, { useContext, useMemo } from 'react';
import { EntityValidations, IGoDefElement, GoDefElement, ISpecificEntityFormProps, GoDefElementOrgLevel1 } from '../../types';
import styles from '../../styles/cr.module.scss';
import { CrTextField } from '../cr/CrTextField';
import { CrNumberTextField } from '../cr/CrNumberTextField';
import { EntityForm } from '../EntityForm';
import { LookupService, NumberService, ValidationService } from '../../services';
import { DataContext } from '../DataContext';
import { CrDropdown } from '../cr/CrDropdown';
import { OrbUserContext } from '../OrbUserContext';

export class DefineSpecificAreaFormValidations extends EntityValidations {
    public RagRatingStyle: string = null;
    public OrgLevel1: string = null;
}

export const DefineSpecificAreaForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { lookupData, loadLookupData: { groups }, dataServices: { goDefElementService, goDefElementOrgLevel1Service } } = useContext(DataContext);
    const { orbConfig } = useContext(OrbUserContext);

    useMemo(() => groups(), [groups]);

    const validateEntity = (goDefElement: IGoDefElement): Promise<DefineSpecificAreaFormValidations> => {
        const errors = new DefineSpecificAreaFormValidations();

        if (goDefElement.Title === null || goDefElement.Title === '' || (goDefElement.Title && goDefElement.Title.length == 0)) {
            errors.Title = 'Name is required';
            errors.Valid = false;
        }
        else
            errors.Title = null;

        if (
            goDefElement.OrgLevel1Filter === 1 &&
            (!goDefElement.GoDefElementOrgLevel1s || goDefElement.GoDefElementOrgLevel1s.length === 0)
        ) {
            errors.OrgLevel1 = `${orbConfig.OrgL1Label} is required`;
            errors.Valid = false;
        } else {
            errors.OrgLevel1 = null;
        }


        if (goDefElement.RagRatingStyle === null) {
            errors.RagRatingStyle = 'Rag Rating Style is required';
            errors.Valid = false;
        } else if (goDefElement.RagRatingStyle !== null && (isNaN(Number(goDefElement.RagRatingStyle)) || Number(goDefElement.RagRatingStyle) <= 0)) {
            errors.RagRatingStyle = 'Rag Rating Style must be a number and greater than 0';
            errors.Valid = false;
        } else if (goDefElement.RagRatingStyle !== null && !ValidationService.validSqlDecimal(Number(goDefElement.RagRatingStyle))) {
            errors.RagRatingStyle = 'Rag Rating Style is too big';
            errors.Valid = false;
        }
        else
            errors.RagRatingStyle = null;

        return Promise.resolve(errors);
    };

    return (
        <EntityForm<IGoDefElement, DefineSpecificAreaFormValidations>
            {...props}
            entityName="Define Specific Area"
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: goDefElement, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            className={styles.formField}
                            required={true}
                            maxLength={255}
                            value={goDefElement.Title}
                            onChange={(ev, newVal) => changeHandlers.changeTextField(newVal, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrDropdown
                            label={`Required For`}
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions([
                                { ID: 0, Title: `All ${orbConfig.OrgL1LabelP}` },
                                { ID: 1, Title: `Specific ${orbConfig.OrgL1LabelP}` },
                            ])}
                            selectedKey={goDefElement.OrgLevel1Filter}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'OrgLevel1Filter')}
                        />
                        {goDefElement.OrgLevel1Filter === 1 &&
                            <CrDropdown
                                label={`${orbConfig.OrgL1LabelP}`}
                                className={styles.formField}
                                multiSelect
                                options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                selectedKeys={goDefElement.GoDefElementOrgLevel1s?.map(x => x.OrgLevel1ID)}
                                onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'GoDefElementOrgLevel1s', new GoDefElementOrgLevel1(), 'OrgLevel1ID')}
                                required={true}
                                errorMessage={errors.OrgLevel1}
                            />
                        }
                        <CrTextField
                            label="Instructions"
                            className={styles.formField}
                            maxLength={1000}
                            rows={3}
                            multiline={true}
                            value={goDefElement.Instructions}
                            onChange={(ev, newVal) => changeHandlers.changeTextField(newVal, 'Instructions')}
                        />
                        <CrTextField
                            label="Full Instructions"
                            className={styles.formField}
                            maxLength={5000}
                            multiline={true}
                            rows={6}
                            value={goDefElement.FullInstructions}
                            onChange={(ev, newVal) => changeHandlers.changeTextField(newVal, 'FullInstructions')}
                        />
                        <CrNumberTextField
                            label="RAG Rating Style (1 or 2)"
                            required={true}
                            className={styles.formField}
                            maxLength={19}
                            value={goDefElement.RagRatingStyle}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'RagRatingStyle')}
                            errorMessage={errors.RagRatingStyle}
                        />
                    </div>
                );
            }}
            loadEntity={id => goDefElementService.read(id, true, true)}
            loadNewEntity={() => new GoDefElement()}
            loadEntityValidations={() => new DefineSpecificAreaFormValidations()}
            onValidateEntity={validateEntity}
            onCreate={u => goDefElementService.create(u)}
            onUpdate={u => goDefElementService.updatePut(u.ID, u)}
            onBeforeSave={goDefElement => goDefElement.RagRatingStyle = NumberService.ToNumberOrNull(goDefElement.RagRatingStyle)}
            childEntities={[
                {
                    ObjectParentProperty: 'GoDefElementOrgLevel1s',
                    ParentIdProperty: 'GoDefElementID',
                    ChildIdProperty: 'OrgLevel1ID',
                    ChildService: goDefElementOrgLevel1Service
                },
            ]}
        />
    );
};
